import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Fuel, TrendingUp, DollarSign, Gauge, Calendar, BarChart3, Activity, Target, Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { formatDistance, formatFuelAmount, formatEfficiency, formatCurrency, getFuelUnitLong, getDistanceUnitLong, getEfficiencyUnitLong } from '../utils/measurements';
import { getBackendUrl, getApiUrl } from '../utils/config';

const BACKEND_URL = getBackendUrl();
const API = getApiUrl();

const FuelFillUp = ({ car, userPreferences, onUpdate, isOwner, hasEditPermission, userPlan }) => {
  const [fillUps, setFillUps] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    fill_date: new Date().toISOString().split('T')[0],
    odometer_reading: car.current_odometer || 0,
    fuel_amount: '',
    fuel_cost: '',
    notes: ''
  });
  
  // Check if user has fuel tracking access (Basic, Premium, or Professional)
  const hasFuelTrackingAccess = userPlan === 'Basic' || userPlan === 'Premium' || userPlan === 'Professional';

  useEffect(() => {
    if (car?.id) {
      fetchFuelData();
    }
  }, [car?.id]);

  const fetchFuelData = async () => {
    try {
      setLoading(true);
      const [fillUpsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/cars/${car.id}/fuel-fillups`),
        axios.get(`${API}/cars/${car.id}/fuel-analytics`)
      ]);
      
      setFillUps(fillUpsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching fuel data:', error);
      toast.error('Failed to load fuel data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user has access before proceeding
    if (!hasFuelTrackingAccess) {
      toast.error('Fuel Tracking is available on Basic plan and above. Upgrade to Basic or Premium to track fuel fill-ups.', {
        duration: 5000,
        icon: '🔒',
      });
      return;
    }
    
    // Prevent double submission
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        odometer_reading: parseInt(formData.odometer_reading),
        fuel_amount: parseFloat(formData.fuel_amount),
        fuel_cost: parseFloat(formData.fuel_cost)
      };

      await axios.post(`${API}/cars/${car.id}/fuel-fillups`, submitData);
      
      toast.success('Fill-up recorded successfully!');
      setShowAddForm(false);
      setFormData({
        fill_date: new Date().toISOString().split('T')[0],
        odometer_reading: submitData.odometer_reading,
        fuel_amount: '',
        fuel_cost: '',
        notes: ''
      });
      
      // Refresh data and notify parent of odometer update
      await fetchFuelData();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error creating fill-up:', error);
      // Check if it's a feature restriction error
      if (error.response?.status === 403 && error.response?.data?.detail?.error === 'feature_restricted') {
        const errorDetail = error.response.data.detail;
        toast.error(errorDetail.message || 'Fuel Tracking is available on Basic plan and above.', {
          duration: 5000,
        });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to record fill-up');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteFillup = async (fillupId) => {
    try {
      await axios.delete(`${API}/cars/${car.id}/fuel-fillups/${fillupId}`);
      toast.success('Fill-up deleted successfully!');
      
      // Refresh data
      await fetchFuelData();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting fill-up:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete fill-up');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Fuel className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-semibold">Fuel Tracking</h3>
        </div>
        
        <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Fill-up
            </Button>
          </DialogTrigger>
          
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Record Fill-up</DialogTitle>
              <DialogDescription>
                Track your fuel consumption and update odometer reading
              </DialogDescription>
            </DialogHeader>
            
            {/* Upgrade Banner for Free Users */}
            {!hasFuelTrackingAccess && (
              <div className="mb-4 p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-xl">🔒</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-orange-900 mb-1">Upgrade to Basic or Premium Required</h4>
                    <p className="text-sm text-orange-700">
                      Fuel Tracking is available on Basic plan and above. Upgrade your subscription to track fuel fill-ups and monitor efficiency.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fill_date">Fill-up Date</Label>
                <Input
                  id="fill_date"
                  type="date"
                  value={formData.fill_date}
                  onChange={(e) => setFormData({...formData, fill_date: e.target.value})}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="odometer_reading">
                  Current Odometer ({getDistanceUnitLong(userPreferences?.measurement_system)})
                </Label>
                <Input
                  id="odometer_reading"
                  type="number"
                  value={formData.odometer_reading}
                  onChange={(e) => setFormData({...formData, odometer_reading: e.target.value})}
                  min={car.current_odometer || 0}
                  required
                />
                <p className="text-xs text-gray-500">
                  This will update your car's current odometer reading
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="fuel_amount">
                  Fuel Amount ({getFuelUnitLong(userPreferences?.measurement_system)})
                </Label>
                <Input
                  id="fuel_amount"
                  type="number"
                  step="0.01"
                  value={formData.fuel_amount}
                  onChange={(e) => setFormData({...formData, fuel_amount: e.target.value})}
                  placeholder="e.g., 45.5"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="fuel_cost">Total Cost</Label>
                <Input
                  id="fuel_cost"
                  type="number"
                  step="0.01"
                  value={formData.fuel_cost}
                  onChange={(e) => setFormData({...formData, fuel_cost: e.target.value})}
                  placeholder="e.g., 65.25"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Gas station, fuel type, etc."
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Saving...</span>
                    </div>
                  ) : (
                    'Record Fill-up'
                  )}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowAddForm(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="analytics" className="space-y-4">
        <TabsList>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="history">Fill-up History</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="space-y-4">
          {analytics && analytics.total_fillups > 0 ? (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center">
                      <TrendingUp className="w-4 h-4 mr-2 text-green-600" />
                      Avg Efficiency
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {formatEfficiency(analytics.average_efficiency, userPreferences?.measurement_system)}
                    </div>
                    <p className="text-xs text-gray-500">
                      {getEfficiencyUnitLong(userPreferences?.measurement_system)}
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center">
                      <Fuel className="w-4 h-4 mr-2 text-blue-600" />
                      Total Fuel
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {formatFuelAmount(analytics.total_fuel, userPreferences?.measurement_system)}
                    </div>
                    <p className="text-xs text-gray-500">
                      {analytics.total_fillups} fill-ups
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center">
                      <DollarSign className="w-4 h-4 mr-2 text-yellow-600" />
                      Total Cost
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-yellow-600">
                      {formatCurrency(analytics.total_cost)}
                    </div>
                    <p className="text-xs text-gray-500">
                      {formatCurrency(analytics.cost_per_distance)} per {userPreferences?.measurement_system === 'metric' ? 'km' : 'mile'}
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center">
                      <Gauge className="w-4 h-4 mr-2 text-purple-600" />
                      Distance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-600">
                      {formatDistance(analytics.total_distance, userPreferences?.measurement_system)}
                    </div>
                    <p className="text-xs text-gray-500">
                      Since tracking started
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Recent Fill-ups Preview */}
              {analytics.recent_fillups && analytics.recent_fillups.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Activity className="w-5 h-5 mr-2" />
                      Recent Efficiency Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analytics.recent_fillups.map((fillup, index) => (
                        <div key={fillup.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">
                              {new Date(fillup.fill_date).toLocaleDateString()}
                            </span>
                            <span className="text-sm">
                              {formatDistance(fillup.odometer_reading, userPreferences?.measurement_system)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {fillup.fuel_efficiency && (
                              <Badge variant="outline">
                                {formatEfficiency(fillup.fuel_efficiency, userPreferences?.measurement_system)}
                              </Badge>
                            )}
                            <span className="text-sm text-gray-600">
                              {formatFuelAmount(fillup.fuel_amount, userPreferences?.measurement_system)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="text-center py-8">
                <Fuel className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">No fuel data yet</h3>
                <p className="text-gray-500 mb-4">Record your first fill-up to start tracking fuel efficiency</p>
                <Button onClick={() => setShowAddForm(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Fill-up
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          {fillUps.length > 0 ? (
            <div className="space-y-4">
              {fillUps.map((fillup) => (
                <Card key={fillup.id}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="font-medium">
                              {new Date(fillup.fill_date).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Gauge className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-600">
                              {formatDistance(fillup.odometer_reading, userPreferences?.measurement_system)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-2">
                        <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-semibold">
                            {formatFuelAmount(fillup.fuel_amount, userPreferences?.measurement_system)}
                          </span>
                          <span className="text-lg font-semibold text-green-600">
                            {formatCurrency(fillup.fuel_cost)}
                          </span>
                        </div>
                        
                        <div className="flex gap-2">
                          {fillup.fuel_efficiency && (
                            <Badge variant="outline" className="text-green-600 border-green-200">
                              {formatEfficiency(fillup.fuel_efficiency, userPreferences?.measurement_system)}
                            </Badge>
                          )}
                          
                          <Badge variant="outline" className="text-yellow-600 border-yellow-200">
                            {formatCurrency(fillup.cost_per_unit)}/
                            {userPreferences?.measurement_system === 'metric' ? 'L' : 'gal'}
                          </Badge>
                        </div>
                        
                        {fillup.distance_traveled && (
                          <span className="text-xs text-gray-500">
                            +{formatDistance(fillup.distance_traveled, userPreferences?.measurement_system)} driven
                          </span>
                        )}
                        </div>
                        
                        {(isOwner || hasEditPermission) && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-1"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Fill-up Record</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this fill-up from {new Date(fillup.fill_date).toLocaleDateString()}? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteFillup(fillup.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete Fill-up
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                    
                    {fillup.notes && (
                      <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-600">
                        {fillup.notes}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-8">
                <Fuel className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">No fill-up history</h3>
                <p className="text-gray-500">Your fuel records will appear here</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FuelFillUp;